import React from 'react';
import { BED_ZONES } from '../contexts/FirestoreBedManagementContext';
import { TrendingUp, TrendingDown, Activity } from 'lucide-react';

const BedOccupancyStats = ({ stats = {} }) => {
  // Provide default values to prevent runtime errors
  const safeStats = {
    available: 0,
    occupied: 0,
    cleaning: 0,
    maintenance: 0,
    reserved: 0,
    total: 0,
    occupancyRate: '0.0',
    byZone: {},
    ...stats
  };

  return (
    <div className="space-y-4">
      {/* Overall Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-green-50 rounded-lg p-3">
          <div className="text-green-600 text-sm font-medium">Available</div>
          <div className="text-2xl font-bold text-green-700">{safeStats.available}</div>
          <div className="text-xs text-green-600">
            {safeStats.total > 0 ? ((safeStats.available / safeStats.total) * 100).toFixed(0) : '0'}%
          </div>
        </div>
        
        <div className="bg-blue-50 rounded-lg p-3">
          <div className="text-blue-600 text-sm font-medium">Occupied</div>
          <div className="text-2xl font-bold text-blue-700">{safeStats.occupied}</div>
          <div className="text-xs text-blue-600">{safeStats.occupancyRate}%</div>
        </div>
        
        <div className="bg-yellow-50 rounded-lg p-3">
          <div className="text-yellow-600 text-sm font-medium">Cleaning</div>
          <div className="text-2xl font-bold text-yellow-700">{safeStats.cleaning}</div>
          <div className="text-xs text-yellow-600">In progress</div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-gray-600 text-sm font-medium">Maintenance</div>
          <div className="text-2xl font-bold text-gray-700">{safeStats.maintenance}</div>
          <div className="text-xs text-gray-600">Offline</div>
        </div>
        
        <div className="bg-purple-50 rounded-lg p-3">
          <div className="text-purple-600 text-sm font-medium">Reserved</div>
          <div className="text-2xl font-bold text-purple-700">{safeStats.reserved}</div>
          <div className="text-xs text-purple-600">Incoming</div>
        </div>
      </div>
      
      {/* Zone-specific Statistics */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Zone Occupancy</h4>
        <div className="space-y-2">
          {Object.entries(BED_ZONES).map(([key, zone]) => {
            const zoneStats = safeStats.byZone?.[zone.id];
            if (!zoneStats) return null;
            
            const occupancyPercent = parseFloat(zoneStats.occupancyRate || '0');
            const isHighOccupancy = occupancyPercent > 80;
            const isLowOccupancy = occupancyPercent < 30;
            
            return (
              <div key={zone.id} className="relative">
                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: zone.color }}
                    />
                    <span className="text-sm font-medium text-gray-700">{zone.name}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">
                      {zoneStats.occupied || 0}/{zoneStats.total || 0}
                    </span>
                    <span className={`text-sm font-medium ${
                      isHighOccupancy ? 'text-red-600' : 
                      isLowOccupancy ? 'text-green-600' : 
                      'text-gray-600'
                    }`}>
                      {zoneStats.occupancyRate || '0.0'}%
                    </span>
                    {isHighOccupancy && <TrendingUp className="h-4 w-4 text-red-500" />}
                    {isLowOccupancy && <TrendingDown className="h-4 w-4 text-green-500" />}
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${zoneStats.occupancyRate || 0}%`,
                      backgroundColor: isHighOccupancy ? '#dc2626' : zone.color
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Occupancy Trend (placeholder for real-time updates) */}
      <div className="pt-3 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center text-sm text-gray-600">
            <Activity className="h-4 w-4 mr-2" />
            <span>Average Length of Stay: 4.2 hours</span>
          </div>
          <div className="text-sm text-gray-600">
            Bed Turnover Rate: 6.8/day
          </div>
        </div>
      </div>
    </div>
  );
};

export default BedOccupancyStats; 